import React, { useState, HTMLAttributes, ReactElement, SyntheticEvent } from 'react';
import classNames from 'classnames';

import './dropdown.scss';

interface DropdownItemInterface {
    title: string | number;
    value: string | number;
    checked?: boolean;
}

interface DropdownProps extends HTMLAttributes<HTMLInputElement> {
    name?: string;
    value?: string | number;
    list?: DropdownItemInterface[];
    readOnly?: boolean;
    showOnHover?: boolean;
    onDropdownChange?: (value: string | number) => void;
}

const Dropdown: React.FC<DropdownProps> = (
    {
        name,
        value: defaultValue,
        placeholder = 'Wybierz z listy',
        list,
        readOnly = false,
        className,
        showOnHover,
        onDropdownChange
    }
): ReactElement => {
    const initList = list
        ?.filter(({ value }): string | number => value)
        .map(({ title, value }): DropdownItemInterface => ({ value, title: title || value }));
    const [active, setActive] = useState(false);
    const [itemList, setItemList] = useState(initList);
    const [defaultItem, setDefaultItem] = useState(itemList?.find(({ value }): boolean => defaultValue === value) || ({
        value: '',
        title: ''
    }));
    const [searchValue, setSearchValue] = useState(defaultItem?.title || defaultItem?.value);
    const dropdownClasses = classNames(className, 'dropdown', { 'dropdown--on-hover': showOnHover });
    const dropdownListClasses = classNames('dropdown__list', { 'dropdown__list--active': active });

    const handleFocus = (): void => {
        if (showOnHover) {
            return;
        }

        setTimeout(() => {
            setActive(!active);
        }, 200);
    };

    const handleBlur = (): void => {
        setTimeout(() => {
            setActive(false);
            setItemList(initList);
            setDefaultItem(prevDefaultItem => {
                setSearchValue(prevDefaultItem?.title || prevDefaultItem?.value);

                return prevDefaultItem;
            });
        }, 200);
    };

    const handleSearch = ({ target }: SyntheticEvent): void => {
        const { value } = target as HTMLInputElement;

        setSearchValue(value);
        setItemList(value ? initList?.filter((item): boolean => String(item.title).toLowerCase().includes(value.toLowerCase())) : initList);
    };

    const handleChange = (item: DropdownItemInterface): void => {
        setSearchValue(item.title || item.value);
        setDefaultItem(item);
        setItemList(itemList?.map((listItem): DropdownItemInterface => listItem.value === defaultItem.value
            ? { ...listItem, checked: true }
            : { ...listItem, checked: false }));

        if (onDropdownChange && item.value !== defaultItem.value) {
            onDropdownChange(item.value);
        }
    };

    return (
        <div className={dropdownClasses} onFocus={handleFocus} onBlur={handleBlur}
            role="button"
        >
            <div className="dropdown__input-wrapper">
                <input
                    className="dropdown__input"
                    type="text"
                    readOnly={readOnly}
                    value={searchValue}
                    placeholder={placeholder}
                    onChange={handleSearch}
                    aria-label={placeholder}
                />
            </div>
            {
                itemList && (
                    <div className={dropdownListClasses}>
                        {
                            itemList.map(({ title, value }) => (
                                <div className="dropdown__list-item-wrapper" key={value}>
                                    <input
                                        className="dropdown__list-item-radio" type="radio" value={value}
                                        name={name}
                                        checked={defaultItem?.value === value}
                                        onChange={(): void => handleChange({ title, value })}
                                        aria-label={(title || value).toString()}
                                    />
                                    <button
                                        type="button"
                                        className="dropdown__list-item-label"
                                        onClick={(): void => handleChange({ title, value })}
                                    >
                                        {title || value}
                                    </button>
                                </div>
                            ))
                        }
                    </div>
                )
            }
        </div>
    );
};

export default Dropdown;
